/* eslint-disable jira/filename-case */
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import without from 'lodash/without';
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/index.tsx';
import {
	isConflictError,
	isNotFoundError,
} from '@jira/polaris__lib-errors/src/isPermissionError.tsx';
import { performDeleteIssueType } from './performDeleteIssueType.tsx';
import type { State } from './IssueType.types.tsx';

type IssueTypeId = string;
type ProjectId = string;

export const deleteIssueType =
	(issueTypeId: IssueTypeId, projectId: ProjectId) =>
	async ({ dispatch }: StoreActionApi<State>): Promise<void> => {
		experience.issueTypes.issueTypeDelete.start();
		try {
			await performDeleteIssueType(issueTypeId, projectId);

			dispatch(deleteIssueTypeFromStateOnly(issueTypeId));

			experience.issueTypes.issueTypeDelete.success();
		} catch (maybeError) {
			const error =
				maybeError instanceof Error ? maybeError : new Error('Error while deleting issue type');

			if (isNotFoundError(error)) {
				experience.issueTypes.issueTypeDelete.abort();
				dispatch(deleteIssueTypeFromStateOnly(issueTypeId));
			} else if (isClientFetchError(error) || isConflictError(error)) {
				experience.issueTypes.issueTypeDelete.abort();
			} else {
				experience.issueTypes.issueTypeDelete.failure(error);
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.issue-types.actions.delete-issue-type',
				'Failed to delete issue type',
				error,
			);

			throw error;
		}
	};

const deleteIssueTypeFromStateOnly =
	(issueTypeId: IssueTypeId) =>
	async ({ setState, getState }: StoreActionApi<State>) => {
		const state = getState();

		setState({
			issueTypesById: omit(state.issueTypesById, issueTypeId),
			issueTypesByProjectId: mapValues(state.issueTypesByProjectId, (issueTypeIds) =>
				without(issueTypeIds, issueTypeId),
			),
		});
	};
